/**
 * ProjectSanitizer.ts
 * Utilitaires pour nettoyer et valider les données de projet avant leur chargement
 * Protège l'application contre les données malformées qui pourraient causer des crashes
 */
import { ShapeDataSchema, type ShapeData, generateShapeId } from '../types/ShapeTypes';
import { logger } from './ProfessionalLogger';

/**
 * Nettoie et valide un tableau de shapes
 *
 * @param shapes - Tableau brut de shapes (potentiellement malformé)
 * @returns Tableau de shapes validées et nettoyées
 *
 * @example
 * ```typescript
 * const rawShapes = [{ id: '1', type: 'rect', x: 10, y: 20 }];
 * const sanitized = sanitizeShapes(rawShapes);
 * ```
 */
export const sanitizeShapes = (shapes: unknown[]): ShapeData[] => {
  // Vérifier que l'entrée est un tableau
  if (!Array.isArray(shapes)) {
    logger.warn("sanitizeShapes: L'entrée n'est pas un tableau, retour d'un tableau vide");
    return [];
  }

  // Valider et nettoyer chaque shape
  const sanitized: ShapeData[] = [];

  for (let i = 0; i < shapes.length; i++) {
    const shape = shapes[i];

    // Ignorer les valeurs null/undefined
    if (shape == null) {
      logger.warn(`sanitizeShapes: Shape à l'index ${i} est null/undefined, ignorée`);
      continue;
    }

    try {
      // Validation avec Zod
      const result = ShapeDataSchema.safeParse(shape);

      if (!result.success) {
        logger.warn(`sanitizeShapes: Shape à l'index ${i} invalide, ignorée:`, {
          errors: result.error.errors,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          shapeId: (shape as any)?.id || 'unknown',
        });
        continue;
      }

      // Sanitization des valeurs avec fallbacks sécurisés
      const cleaned: ShapeData = {
        ...result.data,
        // Garantir un ID unique
        id:
          result.data.id && result.data.id.trim() !== ''
            ? result.data.id.trim()
            : generateShapeId(),

        // Garantir les coordonnées de base
        x: Number.isFinite(result.data.x) ? result.data.x : 0,
        y: Number.isFinite(result.data.y) ? result.data.y : 0,

        // Garantir les dimensions si nécessaires (certains types n'ont pas besoin de width/height)
        ...(result.data.width !== undefined && {
          width:
            Number.isFinite(result.data.width) && result.data.width > 0 ? result.data.width : 100,
        }),
        ...(result.data.height !== undefined && {
          height:
            Number.isFinite(result.data.height) && result.data.height > 0
              ? result.data.height
              : 100,
        }),

        // Garantir la rotation
        ...(result.data.rotation !== undefined && {
          rotation: Number.isFinite(result.data.rotation) ? result.data.rotation : 0,
        }),

        // Garantir l'opacité dans la plage valide
        ...(result.data.opacity !== undefined && {
          opacity: Number.isFinite(result.data.opacity)
            ? Math.max(0, Math.min(1, result.data.opacity))
            : 1,
        }),

        // Garantir la visibilité (par défaut true)
        visible: result.data.visible !== false,

        // Les autres propriétés sont déjà validées par Zod
      };

      sanitized.push(cleaned);
    } catch (error) {
      logger.error(
        `sanitizeShapes: Erreur lors de la validation de la shape à l'index ${i}:`,
        error
      );
      // Continuer avec les autres shapes même en cas d'erreur
    }
  }

  logger.info(`sanitizeShapes: ${sanitized.length}/${shapes.length} shapes validées avec succès`);

  return sanitized;
};
